import React, { useState, useEffect, useRef } from 'react';
import { HelpCircle, X, Send, Sparkles, BookOpen, Play, ChevronRight, Lightbulb, Target, MessageCircle, Bot, Minimize2, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  action?: () => void;
  highlight?: string; // CSS selector for element to highlight
}

interface AITeacherProps {
  currentPage?: string;
}

export const AITeacher: React.FC<AITeacherProps> = ({ currentPage = 'chat' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentTour, setCurrentTour] = useState<TutorialStep[] | null>(null);
  const [tourStep, setTourStep] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [completedTours, setCompletedTours] = useState<string[]>([]);
  const [userProgress, setUserProgress] = useState<{ questionsAsked: number; toursCompleted: number; lastVisit: string }>(
    { questionsAsked: 0, toursCompleted: 0, lastVisit: new Date().toISOString() }
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Page-specific tutorials
  const tutorials: Record<string, TutorialStep[]> = {
    chat: [
      {
        id: 'welcome',
        title: 'Welcome to AI Chat!',
        description: 'This is your AI-powered chat interface. Let me show you around!',
        highlight: '.chat-container'
      },
      {
        id: 'language',
        title: 'Language Toggle',
        description: 'Click the globe icon to switch between English and Portuguese. The entire interface adapts!',
        highlight: '[aria-label="Language toggle"]'
      },
      {
        id: 'devmode',
        title: 'Dev Mode - See the Magic!',
        description: 'Enable Dev Mode to see real-time AI analysis, confidence scores, and processing times.',
        highlight: '[aria-label="Dev mode toggle"]'
      },
      {
        id: 'suggestions',
        title: 'Smart Suggestions',
        description: 'These suggestion chips update based on context. Click them for quick responses!',
        highlight: '.suggestions-container'
      },
      {
        id: 'ai-status',
        title: 'AI System Status',
        description: 'Check the health of AI services, database connections, and Vectorize search in real-time.',
        highlight: '.ai-status-panel'
      },
      {
        id: 'message-input',
        title: 'Send Messages',
        description: 'Type your message here. The AI detects language automatically and responds appropriately.',
        highlight: '.message-input'
      }
    ],
    whatsapp: [
      {
        id: 'whatsapp-welcome',
        title: 'WhatsApp Business Integration',
        description: 'Connect your WhatsApp Business account to automate customer conversations!',
        highlight: '.whatsapp-dashboard'
      },
      {
        id: 'test-mode',
        title: 'Test Mode Toggle',
        description: 'Use Test Mode to simulate WhatsApp messages without needing a real account.',
        highlight: '.test-mode-toggle'
      },
      {
        id: 'webhook-setup',
        title: 'Webhook Configuration',
        description: 'Set up webhooks to receive real-time messages from WhatsApp.',
        highlight: '.webhook-config'
      },
      {
        id: 'message-templates',
        title: 'Message Templates',
        description: 'Create pre-approved message templates for notifications and updates.',
        highlight: '.template-manager'
      },
      {
        id: 'automation-rules',
        title: 'Automation Rules',
        description: 'Define triggers and responses for automatic message handling.',
        highlight: '.automation-panel'
      }
    ],
    trainer: [
      {
        id: 'knowledge',
        title: 'Knowledge Base',
        description: 'Add Q&A pairs here to teach the AI about your business. Each entry improves response accuracy!',
        highlight: '.knowledge-form'
      },
      {
        id: 'intents',
        title: 'Intent Responses',
        description: 'Define how the AI responds to different intentions like greetings, pricing questions, or support.',
        highlight: '.intent-form'
      },
      {
        id: 'vectorize',
        title: 'Semantic Search',
        description: 'Your knowledge is automatically indexed with Vectorize for intelligent semantic search.',
        highlight: '.vectorize-status'
      },
      {
        id: 'multilingual',
        title: 'Multilingual Training',
        description: 'Add responses in multiple languages. The AI automatically uses the right language.',
        highlight: '.language-selector'
      },
      {
        id: 'analytics',
        title: 'Track Performance',
        description: 'Monitor how your training improves AI accuracy and response times.',
        highlight: '.analytics-panel'
      },
      {
        id: 'export-import',
        title: 'Export & Import',
        description: 'Save your training data or import existing knowledge bases.',
        highlight: '.data-management'
      }
    ],
    'ai-features': [
      {
        id: 'cloudflare-ai',
        title: 'CloudFlare AI Models',
        description: 'Powered by Llama 2, DistilBERT, and BGE embeddings for intelligent responses.',
        highlight: '.ai-models-info'
      },
      {
        id: 'vectorize-search',
        title: 'Vectorize Semantic Search',
        description: 'Converts text to 768-dimensional vectors for meaning-based search, not just keywords.',
        highlight: '.vectorize-demo'
      },
      {
        id: 'd1-database',
        title: 'D1 Database',
        description: 'Stores conversations, knowledge, and analytics in CloudFlare\'s edge SQL database.',
        highlight: '.database-info'
      },
      {
        id: 'kv-storage',
        title: 'KV Storage',
        description: 'Fast key-value storage for session data and caching across the edge network.',
        highlight: '.kv-info'
      },
      {
        id: 'r2-storage',
        title: 'R2 Object Storage',
        description: 'Store media files, documents, and backups with CloudFlare R2.',
        highlight: '.r2-info'
      }
    ]
  };

  // Contextual suggestions based on page
  const suggestions: Record<string, string[]> = {
    chat: [
      "How do I enable Dev Mode?",
      "Show me the language features",
      "What is Vectorize?",
      "How does the AI work?"
    ],
    whatsapp: [
      "How do I connect WhatsApp?",
      "What is Test Mode?",
      "How do webhooks work?",
      "Show automation examples"
    ],
    trainer: [
      "How do I add knowledge?",
      "What are intent responses?",
      "How does training work?",
      "Show me best practices"
    ],
    'ai-features': [
      "Explain CloudFlare AI",
      "How does Vectorize work?",
      "What databases are used?",
      "Show me the architecture"
    ],
    general: [
      "What can this system do?",
      "How do I get started?",
      "Show me a demo",
      "Explain the features"
    ]
  };

  useEffect(() => {
    // Load progress from localStorage
    const savedProgress = localStorage.getItem('ai-teacher-progress');
    if (savedProgress) {
      const progress = JSON.parse(savedProgress);
      setUserProgress(progress);
      setCompletedTours(progress.completedTours || []);
    }

    // Load completed tours
    const savedTours = localStorage.getItem('ai-teacher-completed-tours');
    if (savedTours) {
      setCompletedTours(JSON.parse(savedTours));
    }

    // Show welcome message on first visit
    const hasVisited = localStorage.getItem('ai-teacher-visited');
    if (!hasVisited) {
      setTimeout(() => {
        setIsOpen(true);
        addMessage('assistant', getWelcomeMessage());
        localStorage.setItem('ai-teacher-visited', 'true');
      }, 2000);
    } else {
      // Show progress message for returning users
      const daysSinceLastVisit = Math.floor(
        (new Date().getTime() - new Date(userProgress.lastVisit).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastVisit > 0) {
        setTimeout(() => {
          setIsOpen(true);
          addMessage('assistant', `Welcome back! It's been ${daysSinceLastVisit} day${daysSinceLastVisit > 1 ? 's' : ''} since your last visit. You've asked ${userProgress.questionsAsked} questions and completed ${userProgress.toursCompleted} tours. What would you like to learn today?`);
        }, 3000);
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getWelcomeMessage = () => {
    const messages: Record<string, string> = {
      chat: "👋 Hi! I'm your AI Teacher. I can help you learn how to use the chat system. Try asking me about Dev Mode, language switching, or how Vectorize works!",
      trainer: "🧠 Welcome to the AI Training Center! I'll teach you how to make your AI smarter. Ask me about adding knowledge, creating intents, or best practices!",
      general: "✨ Hello! I'm here to help you master this AI system. What would you like to learn about?"
    };
    return messages[currentPage] || messages.general;
  };

  const addMessage = (role: 'user' | 'assistant', content: string) => {
    setMessages(prev => [...prev, { role, content }]);
  };

  const startTour = (tourType: string) => {
    const tour = tutorials[tourType];
    if (tour) {
      setCurrentTour(tour);
      setTourStep(0);
      highlightElement(tour[0].highlight);
    }
  };

  const highlightElement = (selector?: string) => {
    // Remove existing highlights
    document.querySelectorAll('.ai-teacher-highlight').forEach(el => {
      el.classList.remove('ai-teacher-highlight');
    });

    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        element.classList.add('ai-teacher-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const nextTourStep = () => {
    if (currentTour && tourStep < currentTour.length - 1) {
      const nextStep = tourStep + 1;
      setTourStep(nextStep);
      highlightElement(currentTour[nextStep].highlight);
    } else {
      endTour();
    }
  };

  const endTour = () => {
    if (currentTour && currentTour[0]) {
      const tourId = currentTour[0].id;
      const newCompletedTours = [...completedTours, tourId];
      setCompletedTours(newCompletedTours);
      
      // Update progress
      const newProgress = {
        ...userProgress,
        toursCompleted: userProgress.toursCompleted + 1,
        lastVisit: new Date().toISOString()
      };
      setUserProgress(newProgress);
      
      // Save to localStorage
      localStorage.setItem('ai-teacher-completed-tours', JSON.stringify(newCompletedTours));
      localStorage.setItem('ai-teacher-progress', JSON.stringify(newProgress));
      
      // Achievement notification
      if (newProgress.toursCompleted === 1) {
        addMessage('assistant', "🎆 Achievement Unlocked: First Tour Complete! You're on your way to mastering the system!");
      } else if (newProgress.toursCompleted === 5) {
        addMessage('assistant', "🏆 Achievement Unlocked: Tour Expert! You've completed 5 tours!");
      }
    }
    
    setCurrentTour(null);
    setTourStep(0);
    document.querySelectorAll('.ai-teacher-highlight').forEach(el => {
      el.classList.remove('ai-teacher-highlight');
    });
    addMessage('assistant', "Tour complete! 🎉 What else would you like to learn?");
  };

  const processUserMessage = async (message: string) => {
    const lowerMessage = message.toLowerCase();
    
    // Update progress
    const newProgress = {
      ...userProgress,
      questionsAsked: userProgress.questionsAsked + 1,
      lastVisit: new Date().toISOString()
    };
    setUserProgress(newProgress);
    localStorage.setItem('ai-teacher-progress', JSON.stringify(newProgress));
    
    // Achievement for asking questions
    if (newProgress.questionsAsked === 10) {
      addMessage('assistant', "💡 Achievement Unlocked: Curious Learner! You've asked 10 questions!");
    } else if (newProgress.questionsAsked === 50) {
      addMessage('assistant', "🎓 Achievement Unlocked: Knowledge Seeker! 50 questions asked!");
    }
    
    // Check for tour requests
    if (lowerMessage.includes('tour') || lowerMessage.includes('show me around')) {
      startTour(currentPage);
      addMessage('assistant', `Starting the ${currentPage} tour! Follow the highlights...`);
      return;
    }

    // Contextual responses based on keywords
    if (lowerMessage.includes('dev mode')) {
      addMessage('assistant', "Dev Mode is amazing! 🔧 Click the 'Dev Mode' button in the header to see:\n\n• Real-time AI analysis\n• Language detection\n• Sentiment scores\n• Processing times\n• Vectorize search results\n\nIt shows you exactly how the AI is thinking!");
      return;
    }

    if (lowerMessage.includes('language') || lowerMessage.includes('portuguese')) {
      addMessage('assistant', "🌐 Language switching is seamless! Click the globe button to toggle between English and Portuguese. Everything changes:\n\n• UI text and labels\n• AI responses\n• Suggestions\n• Error messages\n\nThe AI automatically detects language in messages too!");
      return;
    }

    if (lowerMessage.includes('vectorize')) {
      addMessage('assistant', "🧠 Vectorize is CloudFlare's semantic search engine! It:\n\n• Converts text to mathematical vectors (embeddings)\n• Finds similar content by meaning, not just keywords\n• Powers contextual responses\n• Gets smarter with each knowledge item\n\nWhen you add knowledge, it's automatically indexed for semantic search!");
      return;
    }

    if (lowerMessage.includes('knowledge') || lowerMessage.includes('training')) {
      addMessage('assistant', "📚 Training the AI is easy!\n\n1. Go to AI Trainer (/ai-trainer)\n2. Add Q&A pairs in Knowledge Base\n3. Define Intent Responses\n4. The AI instantly uses your training\n\nEach item is stored in D1 database AND indexed in Vectorize for semantic search!");
      return;
    }

    if (lowerMessage.includes('best practice')) {
      addMessage('assistant', "✨ Best Practices for AI Training:\n\n• Add variations of common questions\n• Use both languages for global support\n• Keep answers concise and clear\n• Add context-specific responses\n• Test regularly in chat\n• Monitor analytics for improvements\n\nThe more diverse your training data, the smarter your AI becomes!");
      return;
    }

    if (lowerMessage.includes('whatsapp') || lowerMessage.includes('webhook')) {
      addMessage('assistant', "📱 WhatsApp Integration Guide:\n\n1. **Test Mode**: Start here! No account needed\n2. **Production Setup**:\n   • Get WhatsApp Business API access\n   • Configure webhooks with your URL\n   • Add phone number and access token\n3. **Automation**: Set up rules for:\n   • Welcome messages\n   • FAQ responses\n   • Order status\n   • Support tickets\n\nVisit /whatsapp-test to try it now!");
      return;
    }

    if (lowerMessage.includes('cloudflare') || lowerMessage.includes('architecture')) {
      addMessage('assistant', "🏗️ System Architecture:\n\n**Edge Computing Stack:**\n• **Workers**: Serverless functions at the edge\n• **AI Models**: Llama 2, DistilBERT, BGE embeddings\n• **Vectorize**: 768-dim semantic search\n• **D1**: Edge SQL database\n• **KV**: Key-value storage\n• **R2**: Object storage\n\n**Benefits:**\n• Global low latency (< 50ms)\n• Auto-scaling\n• No servers to manage\n• Built-in security\n\nEverything runs on CloudFlare's edge network!");
      return;
    }

    if (lowerMessage.includes('test mode')) {
      addMessage('assistant', "🧪 Test Mode is perfect for development!\n\n**Features:**\n• Mock WhatsApp API responses\n• Simulate incoming messages\n• Test automation rules\n• No credentials required\n• Instant feedback\n\n**How to use:**\n1. Go to /whatsapp-test\n2. Toggle 'Test Mode' ON (purple)\n3. Send test messages\n4. Click 'Simulate Incoming'\n5. Watch the AI respond!\n\nWhen ready, toggle OFF and add real credentials.");
      return;
    }

    if (lowerMessage.includes('automation') || lowerMessage.includes('rules')) {
      addMessage('assistant', "🤖 Automation Examples:\n\n**Customer Service:**\n• Auto-reply to common questions\n• Route to human agents for complex issues\n• Send order confirmations\n\n**Sales:**\n• Product recommendations\n• Price quotes\n• Appointment scheduling\n\n**Marketing:**\n• Welcome sequences\n• Promotional messages\n• Survey collection\n\n**Setup:** Go to AI Trainer → Add intents → Test in chat!");
      return;
    }

    // Interactive Q&A for common questions
    if (lowerMessage.includes('how') && lowerMessage.includes('connect')) {
      addMessage('assistant', "🔗 Connecting Services:\n\n**WhatsApp:** /whatsapp-test → Toggle Test Mode OFF → Add credentials\n**Facebook:** /facebook-test → Configure Page Access Token\n**API:** Use https://biig-api.b-9f2.workers.dev/api/\n\nNeed help with a specific integration?");
      return;
    }

    if (lowerMessage.includes('error') || lowerMessage.includes('problem') || lowerMessage.includes('not working')) {
      addMessage('assistant', "🔧 Troubleshooting Guide:\n\n**Common Issues:**\n1. **Connection Failed**: Check API status at /api/health\n2. **Messages Not Sending**: Verify credentials and webhooks\n3. **AI Not Responding**: Check AI Status panel in Dev Mode\n4. **Language Issues**: Clear cache and refresh\n\n**Quick Fixes:**\n• Toggle Test Mode and try again\n• Check browser console (F12)\n• Clear localStorage\n• Refresh the page\n\nStill having issues? Describe the specific problem.");
      return;
    }

    if (lowerMessage.includes('api') && (lowerMessage.includes('endpoint') || lowerMessage.includes('route'))) {
      addMessage('assistant', "🌐 API Endpoints:\n\n**Base URL:** https://biig-api.b-9f2.workers.dev\n\n**Core Endpoints:**\n• /api/health - System status\n• /api/chat/message - Send chat messages\n• /api/ai-enhanced/chat - AI-powered chat\n• /api/knowledge - Manage knowledge base\n• /api/whatsapp-mock/* - WhatsApp testing\n\n**Authentication:** Currently open (add auth in production)\n**Rate Limits:** 1000 req/min\n**Format:** JSON");
      return;
    }

    if (lowerMessage.includes('security') || lowerMessage.includes('privacy')) {
      addMessage('assistant', "🔒 Security & Privacy:\n\n**Data Protection:**\n• All data encrypted in transit (TLS)\n• CloudFlare edge security\n• No data leaves your region\n• GDPR compliant infrastructure\n\n**Best Practices:**\n• Never commit API keys\n• Use environment variables\n• Enable webhook verification\n• Implement rate limiting\n• Regular security audits\n\n**Compliance:** CloudFlare is SOC 2, ISO 27001 certified");
      return;
    }

    if (lowerMessage.includes('performance') || lowerMessage.includes('speed') || lowerMessage.includes('optimize')) {
      addMessage('assistant', "⚡ Performance Optimization:\n\n**Current Performance:**\n• API Response: < 50ms (edge)\n• AI Processing: 200-500ms\n• Vectorize Search: < 100ms\n\n**Optimization Tips:**\n1. Use Test Mode for development\n2. Enable caching in KV storage\n3. Batch API requests\n4. Minimize knowledge base queries\n5. Use webhook queues for scale\n\n**Monitoring:** Check Dev Mode for real-time metrics!");
      return;
    }

    if (lowerMessage.includes('cost') || lowerMessage.includes('pricing') || lowerMessage.includes('free')) {
      addMessage('assistant', "💰 CloudFlare Pricing:\n\n**Free Tier Includes:**\n• 100k Workers requests/day\n• 1M KV reads/month\n• 10GB R2 storage\n• 1k AI requests/day\n• 30k Vectorize queries/month\n\n**Perfect for:**\n• Development\n• Small businesses\n• Testing\n\n**Scale:** Pay-as-you-go for additional usage\nNo upfront costs, no minimums!");
      return;
    }

    if (lowerMessage.includes('learn more') || lowerMessage.includes('documentation')) {
      addMessage('assistant', "📚 Learning Resources:\n\n**Quick Start:**\n1. Complete the interactive tour\n2. Try Test Mode with mock data\n3. Add your first knowledge items\n4. Test with real messages\n\n**Documentation:**\n• README.md - Setup guide\n• /docs - API documentation\n• WHATSAPP_TESTING_GUIDE.md\n\n**Get Help:**\n• Ask me anything!\n• Check Dev Mode for debugging\n• GitHub Issues for bugs\n\nWhat specific topic interests you?");
      return;
    }

    // Check for follow-up questions
    if (lowerMessage.includes('tell me more') || lowerMessage.includes('explain')) {
      const context = messages.length > 0 ? messages[messages.length - 1].content : '';
      if (context.includes('Vectorize')) {
        addMessage('assistant', "🧬 Deep Dive: Vectorize\n\n**How it works:**\n1. Text is converted to 768-dimensional vectors\n2. Each dimension represents semantic meaning\n3. Similar concepts have similar vectors\n4. Cosine similarity finds matches\n\n**Example:** 'car' and 'automobile' have high similarity even though they're different words!\n\n**Your data:** Each knowledge item creates a vector for instant semantic search.");
      } else if (context.includes('CloudFlare')) {
        addMessage('assistant', "🌐 CloudFlare Edge Platform:\n\n**Global Network:**\n• 275+ cities worldwide\n• < 50ms latency for 95% of users\n• Automatic failover\n• DDoS protection included\n\n**Your app runs everywhere simultaneously!** No single point of failure.");
      } else {
        addMessage('assistant', "What specific aspect would you like me to explain in more detail? I can go deeper on any topic!");
      }
      return;
    }

    // Default response with enhanced context
    setIsTyping(true);
    try {
      // First, try to find relevant information from our knowledge base
      const knowledgeResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/ai-enhanced/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: message,
          limit: 3
        })
      });

      if (knowledgeResponse.ok) {
        const searchResults = await knowledgeResponse.json();
        if (searchResults.results && searchResults.results.length > 0) {
          const relevantInfo = searchResults.results[0];
          addMessage('assistant', `💡 Based on our knowledge base:\n\n${relevantInfo.answer || relevantInfo.response}\n\n${relevantInfo.confidence > 0.8 ? 'This is from verified training data.' : 'Would you like more details?'}`);
          setIsTyping(false);
          return;
        }
      }

      // Fallback to AI response
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          context: {
            sessionId: 'teacher',
            isTeaching: true,
            currentPage,
            questionsAsked: userProgress.questionsAsked,
            toursCompleted: userProgress.toursCompleted
          }
        })
      });

      const data = await response.json();
      addMessage('assistant', data.response || "I'm here to help! Try asking about specific features or request a tour.");
    } catch (error) {
      // Intelligent fallback based on context
      const fallbackResponses = [
        "Let me help you explore the features! Try asking about:\n• How to enable Dev Mode\n• WhatsApp integration\n• AI training\n• Language switching\n\nOr request a guided tour!",
        "I'm your AI Teacher! I can explain:\n• System architecture\n• CloudFlare services\n• API endpoints\n• Best practices\n\nWhat interests you most?",
        "Ready to learn? I can show you:\n• Interactive tours\n• Feature explanations\n• Troubleshooting tips\n• Performance optimization\n\nJust ask!"
      ];
      const randomFallback = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
      addMessage('assistant', randomFallback);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      addMessage('user', inputValue);
      processUserMessage(inputValue);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    handleSend();
  };

  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow"
        title="AI Teacher - Learn how to use this system"
      >
        <div className="relative">
          <Lightbulb size={24} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></span>
        </div>
      </motion.button>
    );
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed z-50 bg-white rounded-2xl shadow-2xl ${
              isMinimized 
                ? 'bottom-6 right-6 w-80 h-16' 
                : 'bottom-6 right-6 w-96 h-[600px]'
            } flex flex-col transition-all duration-300`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Bot size={20} />
                <span className="font-semibold">AI Teacher</span>
                {userProgress.toursCompleted > 0 && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                    🏆 {userProgress.toursCompleted}
                  </span>
                )}
                {isTyping && (
                  <span className="text-xs opacity-75">thinking...</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Tour Banner */}
                {currentTour && (
                  <div className="bg-purple-50 p-3 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-purple-900">
                          Step {tourStep + 1} of {currentTour.length}: {currentTour[tourStep].title}
                        </p>
                        <p className="text-sm text-purple-700 mt-1">
                          {currentTour[tourStep].description}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={nextTourStep}
                          className="px-3 py-1 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600"
                        >
                          {tourStep < currentTour.length - 1 ? 'Next' : 'Finish'}
                        </button>
                        <button
                          onClick={endTour}
                          className="px-3 py-1 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: msg.role === 'user' ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-xl ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-xl px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Suggestions */}
                {showSuggestions && messages.length === 0 && (
                  <div className="px-4 pb-2">
                    <p className="text-xs text-gray-500 mb-2">Try these:</p>
                    <div className="flex flex-wrap gap-2">
                      {(suggestions[currentPage] || suggestions.general).map((suggestion: string, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs hover:bg-purple-100 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="px-4 pb-2 flex gap-2">
                  <button
                    onClick={() => startTour(currentPage)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100"
                  >
                    <Play size={14} />
                    {completedTours.includes(currentPage) ? 'Replay' : 'Start'} Tour
                  </button>
                  <button
                    onClick={() => {
                      addMessage('user', 'Show me best practices');
                      processUserMessage('Show me best practices');
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100"
                  >
                    <BookOpen size={14} />
                    Best Practices
                  </button>
                  <button
                    onClick={() => {
                      addMessage('user', 'What can I do here?');
                      processUserMessage('What can I do here?');
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm hover:bg-purple-100"
                  >
                    <HelpCircle size={14} />
                    Help
                  </button>
                </div>

                {/* Input */}
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Ask me anything about this system..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global styles for highlighting */}
      <style>{`
        .ai-teacher-highlight {
          position: relative;
          box-shadow: 0 0 0 4px rgba(147, 51, 234, 0.3), 0 0 20px rgba(147, 51, 234, 0.4) !important;
          animation: pulse-highlight 2s infinite;
          z-index: 40;
        }
        
        @keyframes pulse-highlight {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(147, 51, 234, 0.3), 0 0 20px rgba(147, 51, 234, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(147, 51, 234, 0.2), 0 0 30px rgba(147, 51, 234, 0.6);
          }
        }
      `}</style>
    </>
  );
};